import { ArrowDown, ArrowUp, Copy, GripVertical, Trash2 } from 'lucide-react';

interface PreviewToolbarProps {
  /** Block display name shown in the label chip (e.g. "Card"). */
  label: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** false when the block is `locked.delete`. */
  canDelete: boolean;
  /** false for seeded-only blocks the user couldn't add another of. */
  canDuplicate: boolean;
  /**
   * Sortable listeners/attributes spread onto the grip in the label chip,
   * making it the preview's drag handle. Undefined = not draggable
   * (`locked.reorder`) — no grip is shown.
   */
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

/**
 * Floating controls on the selected block in the preview: label chip on the
 * left, move/duplicate/delete on the right.
 */
export default function PreviewToolbar({
  label,
  canMoveUp,
  canMoveDown,
  canDelete,
  canDuplicate,
  dragHandleProps,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
}: PreviewToolbarProps) {
  const action =
    (fn: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
      // Keep toolbar clicks from re-selecting (or selecting a parent).
      e.stopPropagation();
      fn();
    };

  return (
    // Sits fully above the block (bottom-full), spanning the selection
    // outline's outer edges (the ±2px offsets match the 2px outline), so the
    // chips' bottoms land flush on the outline's top edge. Only the tops are
    // rounded — the bottoms merge into the purple frame.
    <div className="font-montserrat pointer-events-none absolute -right-0.5 bottom-full -left-0.5 z-20 flex items-end justify-between">
      <span className="pointer-events-auto flex items-center gap-1 rounded-t-md bg-violet-500 py-1 pr-2.5 pl-1.5 text-xs font-medium text-white">
        {dragHandleProps && (
          <button
            type="button"
            aria-label={`Drag to reorder ${label}`}
            {...dragHandleProps}
            className="cursor-grab touch-none text-violet-200 hover:text-white active:cursor-grabbing"
          >
            <GripVertical size={12} />
          </button>
        )}
        {label}
      </span>
      {/* No gaps or inner padding: each button is a full-height cell so its
          hover fill runs flush to the bar's edges; overflow-hidden clips the
          fill to the rounded top corners. */}
      <div className="pointer-events-auto flex items-stretch overflow-hidden rounded-t-md bg-violet-500 text-white">
        <button
          type="button"
          aria-label={`Move ${label} up`}
          disabled={!canMoveUp}
          onClick={action(onMoveUp)}
          className="px-2 py-1.5 hover:bg-violet-700 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ArrowUp size={14} />
        </button>
        <button
          type="button"
          aria-label={`Move ${label} down`}
          disabled={!canMoveDown}
          onClick={action(onMoveDown)}
          className="px-2 py-1.5 hover:bg-violet-700 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ArrowDown size={14} />
        </button>
        {canDuplicate && (
          <button
            type="button"
            aria-label={`Duplicate ${label}`}
            onClick={action(onDuplicate)}
            className="px-2 py-1.5 hover:bg-violet-700"
          >
            <Copy size={14} />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            aria-label={`Delete ${label}`}
            onClick={action(onDelete)}
            className="px-2 py-1.5 hover:bg-violet-700"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
